import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/shared/auth/auth.service';
import { LoginModalComponent } from '../login-modal/login-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GetInTouchComponent } from '../get-in-touch/get-in-touch.component';

@Component({
  selector: 'app-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss'],
  animations: [
    trigger('fadeAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class PricingComponent implements AfterViewInit, OnDestroy {
  @ViewChild('loginModal') loginModal: LoginModalComponent;
  @ViewChild('gridSlider') gridSlider: ElementRef<HTMLElement>;
  @ViewChild('gridStorySlider') gridStorySlider: ElementRef<HTMLElement>;
  @ViewChild('verticlesliderRef') verticleSlider: ElementRef<HTMLElement>;
  @ViewChild('beyondGridSection') beyondGridSection: ElementRef<HTMLElement>;

  currentSlide: number = 1;
  slider2: KeenSliderInstance = null;
  slider3: KeenSliderInstance = null;
  verticleSliderInstance: KeenSliderInstance = null;
  autoSwitchInterval: any;
  pricingCalculateStarted: boolean = false;
  growthPlusSelected: boolean = true;
  private savedScrollPosition: number = 0;

  constructor(
    private renderer: Renderer2,
    private authService: AuthService,
    private router: Router,
    private modalService: NgbModal
  ) { }

  ngAfterViewInit() {
    // Initialize the second slider with breakpoints
    this.slider2 = new KeenSlider(this.gridSlider.nativeElement, {
      breakpoints: {
        '(max-width: 1650px)': {
          slides: { perView: 3, spacing: 14 },
        },
        '(max-width: 1199px)': {
          loop: true,
          slides: { perView: 2.5, spacing: 14, origin: 'center' },
        },
        '(max-width: 767px)': {
          loop: true,
          slides: { perView: 1.48, spacing: 6, origin: 'center' },
        },
      },
      slides: { perView: 3, spacing: 24 },
      initial: 0,
      created: (s) => {
        // Set initial active state
        if (s.options.loop) {
          // Remove both classes when in loop mode
          this.renderer.removeClass(this.beyondGridSection.nativeElement, 'active-left');
          this.renderer.removeClass(this.beyondGridSection.nativeElement, 'active-right');
        }
        this.renderer.addClass(this.beyondGridSection.nativeElement, 'active-left');
      },
      slideChanged: (s) => {
        const details = s.track.details;
        // Check if loop mode is active (will be true for smaller screens)
        if (s.options.loop) {
          // Remove both classes when in loop mode
          this.renderer.removeClass(this.beyondGridSection.nativeElement, 'active-left');
          this.renderer.removeClass(this.beyondGridSection.nativeElement, 'active-right');
        } else {
          // Original logic for non-loop mode
          if (details.rel > 0) {
            this.renderer.removeClass(this.beyondGridSection.nativeElement, 'active-left');
            this.renderer.addClass(this.beyondGridSection.nativeElement, 'active-right');
          } else {
            this.renderer.removeClass(this.beyondGridSection.nativeElement, 'active-right');
            this.renderer.addClass(this.beyondGridSection.nativeElement, 'active-left');
          }
        }
      }
    });

    this.slider3 = new KeenSlider(this.gridStorySlider.nativeElement, {
      breakpoints: {
        '(max-width: 1650px)': {
          slides: { perView: 2, spacing: 14 },
        },
        '(max-width: 1199px)': {
          slides: { perView: 2, spacing: 14 },
        },
        '(max-width: 767px)': {
          slides: { perView: 1, spacing: 16 },
        },
      },
      slides: { perView: 2, spacing: 24 },
    });

    this.initializeVerticleSlider();
    // window.addEventListener('resize', this.onResize.bind(this));
  }

  handleRegisterBizClick(onlyLogin: boolean = false): void {
    const returnUrl = onlyLogin ? '/' : '/onboarding';

    if (this.authService.isLoggedIn()) {
      this.router.navigate([returnUrl]);
    } else {
      // Store the return URL before opening the login modal
      this.authService.updateReturnURL(returnUrl);
      this.loginModal.openModal(this.loginModal.loginTemplate);
    }
  }

  initializeVerticleSlider() {
    if (!this.verticleSliderInstance) {
      this.verticleSliderInstance = new KeenSlider(
        this.verticleSlider.nativeElement,
        {
          breakpoints: {
            '(max-width: 1650px)': {
              slides: { perView: 1, spacing: 20 },
            },
            '(max-width: 1199px)': {
              slides: { perView: 1, spacing: 20 },
            },
            '(max-width: 767px)': {
              slides: { perView: 1, spacing: 10 },
            },
          },
          slides: { perView: 1, spacing: 20 },
        }
      );
      this.startAutoSwitch();
    }
  }

  startAutoSwitch() {
    if (this.autoSwitchInterval) {
      clearInterval(this.autoSwitchInterval);
    }

    this.autoSwitchInterval = setInterval(() => {
      if (this.verticleSliderInstance) {
        const currentSlide = this.verticleSliderInstance.track.details.rel;
        const totalSlides = this.verticleSliderInstance.track.details.slides.length;

        // Move to the next slide
        this.verticleSliderInstance.moveToIdx((currentSlide + 1) % totalSlides);
      }
    }, 4000);
  }

  destroyVerticleSlider() {
    if (this.verticleSliderInstance) {
      this.verticleSliderInstance.destroy();
      this.verticleSliderInstance = null;
    }
  }

  // onResize() {
  //   this.initializeVerticleSlider();
  // }

  updateActiveClass(sliderElement: HTMLElement, activeIndex: number) {
    const slides = sliderElement.querySelectorAll('.keen-slider__slide');
    slides.forEach((slide, index) => {
      if (index === activeIndex) {
        this.renderer.addClass(slide, 'active');
      } else {
        this.renderer.removeClass(slide, 'active');
      }
    });
  }

  startPricingCalculation() {
    // Save the current scroll position before showing calculator
    this.savedScrollPosition = window.scrollY;
    this.pricingCalculateStarted = true;
  }

  closeCalculator() {
    this.pricingCalculateStarted = false;

    // After the state changes and animation completes
    setTimeout(() => {
      // Restore the saved scroll position
      window.scrollTo({
        top: this.savedScrollPosition,
        behavior: 'smooth'
      });
    }, 300); // Wait for fade animation to complete
  }

  ngOnDestroy() {
    if (this.slider2) this.slider2.destroy();
    if (this.slider3) this.slider3.destroy();
    this.destroyVerticleSlider();
    // window.removeEventListener('resize', this.onResize.bind(this));
  }

  openGetInTouchModal() {
    this.modalService.open(GetInTouchComponent, {
      backdropClass: 'primary-backdrop',
      windowClass: 'project-modal custom-form-modal',
      centered: true,
      backdrop: 'static', // Prevent closing on backdrop click
      keyboard: false // Prevent closing on ESC key
    });
  }
}

